import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText, Printer, Lightbulb, AlertTriangle } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { buildReport, exportPDF, exportXLSX, exportCSV, exportOnePage } from "../lib/reports";
import { computeMonths, sortedMonths } from "../lib/finance";
import { fmt, monthLabel, dateLabel } from "../lib/format";
import { PageHeader, Section, NativeSelect, Money, Empty, Banner } from "../components/common";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import ImportReport from "../components/ImportReport";
import TaxPack from "../components/TaxPack";

function T({ head, rows, testid }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground">No data.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="text-xs uppercase text-muted-foreground border-b"><tr>{head.map((h, i) => <th key={h} className={`p-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b last:border-0">{r.map((c, j) => <td key={j} className={`p-2 ${j ? "text-right" : ""}`}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function MonthlyReport() {
  const { state } = useStore();
  const keys = sortedMonths(computeMonths(state));
  const [sel, setSel] = useState(null);
  const month = sel || keys[keys.length - 1];
  const r = useMemo(() => (month ? buildReport(state, month) : null), [state, month]);
  if (!month) return (<div><Section><Empty icon={FileText} title="No data to report yet" text="Import a statement to generate your first monthly report." /></Section></div>);
  const run = (fn, label) => { try { fn(state, month); toast.success(`${label} downloaded`); } catch (e) { console.error(e); toast.error(`Could not create ${label}`); } };
  const M = (v) => <Money value={v} />;
  return (
    <div>
      <PageHeader title="Monthly report" subtitle="A complete household financial report, generated from your data." testid="monthly-report-title">
        <NativeSelect value={month} onChange={setSel} testid="report-month-select">{[...keys].reverse().map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}</NativeSelect>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-6 no-print">
        <Button onClick={() => run(exportPDF, "PDF report")} data-testid="export-pdf-button"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
        <Button variant="outline" onClick={() => run(exportXLSX, "Excel report")} data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
        <Button variant="outline" onClick={() => run(exportCSV, "CSV")} data-testid="export-csv-button"><FileText className="h-4 w-4 mr-1" /> CSV</Button>
        <Button variant="outline" onClick={() => run(exportOnePage, "One-page summary")} data-testid="export-onepage-button"><FileDown className="h-4 w-4 mr-1" /> One-page budget summary</Button>
        <Button variant="ghost" onClick={() => window.print()} data-testid="print-report-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
      </div>
      <div className="space-y-5 print-area" data-testid="report-preview">
        <Section title={`1. Executive summary — ${monthLabel(month)}`}>
          {r.preliminary && <Banner icon={AlertTriangle} tone="warning">Budget is preliminary (fewer than 3 months of data).</Banner>}
          <p className="text-base leading-relaxed mt-3" data-testid="report-summary">{r.summary}</p>
        </Section>
        <div className="grid md:grid-cols-2 gap-5">
          <Section title="2. Income & expenses">
            <T testid="report-totals" head={["Measure", "Amount"]} rows={[["Total income", M(r.totals.income)], ["Total expenses", M(r.totals.expense)], ["Savings contributions", M(r.totals.savings)], ["Net cash flow", <Money value={r.totals.net} signed />], ["Savings rate", `${r.totals.savingsRate || 0}%`]]} />
          </Section>
          <Section title="3. Spending by category">
            <T head={["Category", "Amount"]} rows={r.byCat.slice(0, 12).map((c) => [c.name, M(c.amount)])} />
          </Section>
        </div>
        <Section title="4. Budget vs actual">
          <T head={["Category", "Budget", "Actual", "Remaining"]} rows={r.bva.map((b) => [b.name, M(b.available), M(b.actual), <span className={b.remaining < 0 ? "text-expense num" : "num"}>{fmt(b.remaining)}</span>])} />
        </Section>
        <div className="grid md:grid-cols-2 gap-5">
          <Section title="5. Recurring bills"><T head={["Bill", "Amount", "Frequency", "Status"]} rows={r.bills.map((b) => [b.name, M(b.amount), b.frequency, b.status])} /></Section>
          <Section title="6. Debt payments" subtitle={`Debt-category payments this month: ${fmt(r.debtPaid)}`}><T head={["Creditor", "Balance", "APR", "Est. payoff"]} rows={r.debts.map((d) => [d.creditor, M(d.balance), `${d.rate}%`, d.payoff === "—" ? "—" : monthLabel(d.payoff)])} /></Section>
          <Section title="7. Savings progress"><T head={["Goal", "Saved", "Target", "Completion"]} rows={r.goals.map((g) => [g.name, M(g.current), M(g.target), g.proj.done ? "Complete" : g.proj.date ? monthLabel(g.proj.date) : "—"])} /></Section>
          <Section title="8. Cash flow forecast" subtitle="Estimates"><T head={["Window", "Income", "Bills", "Balance"]} rows={r.forecast.windows.map((w) => [`${w.days} days`, M(w.income), M(w.bills + w.variable), M(w.endBalance)])} /></Section>
        </div>
        <Section title="9. Unusual transactions"><T head={["Transaction", "Date", "Amount"]} rows={r.unusual.map((t) => [<span>{t.description}<span className="block text-xs text-muted-foreground">{t.reason}</span></span>, dateLabel(t.date), M(t.amount)])} /></Section>
        <Section title="10. Recommendations for next month" testid="report-recommendations">
          {r.recs.length ? <ul className="space-y-3">{r.recs.map((x) => <li key={x.id} className="flex gap-3"><Lightbulb className="h-5 w-5 text-primary shrink-0" /><div><p className="font-semibold text-sm">{x.title}</p><p className="text-sm text-muted-foreground">{x.text}</p></div></li>)}</ul> : <p className="text-sm text-muted-foreground">No recommendations — your spending looks steady.</p>}
        </Section>
      </div>
    </div>
  );
}

export default function Reports() {
  const [params] = useSearchParams();
  return (
    <div data-testid="reports-page">
      <Tabs defaultValue={["import", "tax"].includes(params.get("tab")) ? params.get("tab") : "monthly"}>
        <TabsList className="mb-5 no-print max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="monthly" data-testid="tab-monthly-report">Monthly report</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import-report">Yearly import report</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax-pack">Year-end tax pack</TabsTrigger>
        </TabsList>
        <TabsContent value="monthly"><MonthlyReport /></TabsContent>
        <TabsContent value="import">
          <PageHeader title="Yearly import report" subtitle="Everything imported for the year: totals, transfers we matched, coverage, and what still needs your attention." testid="import-report-title" />
          <ImportReport initialYear={params.get("year")} />
        </TabsContent>
        <TabsContent value="tax">
          <PageHeader title="Year-end tax pack" subtitle="Business expenses, charitable donations and medical costs for the year — ready to hand to your tax preparer." testid="tax-pack-title" />
          <TaxPack />
        </TabsContent>
      </Tabs>
    </div>
  );
}
